import { isNullOrEmpty } from '../../helpers/common';
import { mergeOptions } from '../../helpers/common/merge-options';
import { fetchHtml } from '../../helpers/http/fetch-html';
import { removeQueryStringFromPath } from '../../helpers/url/remove-query-string-from-path';

interface TabElement {
	index: number;
	navigation: HTMLElement;
	element: HTMLElement;
	loaded: boolean;
	target: string;
}

export interface TabsOptions {
	navigationSelector: string;
	wrapperSelector: string;
	contentSelector: string;
	activeClass: string;
	visibleClass: string;
	loadingHtml: string;
	contentIdentifierAttribute: string;
	navigationToContentAttribute: string;
	targetAttribute: string;
	updateHistoryOnTabChange?: boolean;
}

export class Tabs {
	static options: TabsOptions = {
		navigationSelector: '.js-tab__link',
		wrapperSelector: '.js-tab__wrapper',
		contentSelector: '.js-tab__content',
		activeClass: 'is-active',
		visibleClass: 'is-visible',
		loadingHtml: `<div class="c-loading-indicator">
						<div class="c-loading-indicator__element">
							<div class="c-loading-indicator__element__icon"></div>
						</div>
					</div>`,
		contentIdentifierAttribute: 'data-tab',
		navigationToContentAttribute: 'data-tab-action',
		targetAttribute: 'data-target',
		updateHistoryOnTabChange: false,
	};

	private wrapperElement: HTMLElement | null = null;
	private tabElements: TabElement[] = [];
	private visibleTabIndex: number | null = null;
	private options: TabsOptions;

	constructor(public element: HTMLElement, options?: Partial<TabsOptions>) {
		this.options = mergeOptions(Tabs, options, element);

		let index = 0;
		this.wrapperElement = this.element.querySelector(this.options.wrapperSelector);

		this.element
			.querySelectorAll<HTMLElement>(this.options.navigationSelector)
			.forEach((navigation) => {
				const name = navigation.getAttribute(
					this.options.navigationToContentAttribute
				);
				const element = this.element.querySelector<HTMLElement>(
					`${this.options.contentSelector}[${this.options.contentIdentifierAttribute}="${name}"]`
				);

				if (!element) {
					return;
				}

				const target = navigation.getAttribute(this.options.targetAttribute) || navigation.getAttribute('href') || '';
				const loaded = navigation.classList.contains(this.options.activeClass) || isNullOrEmpty(target);

				this.tabElements.push({
					index: index++,
					navigation,
					element,
					loaded,
					target,
				});
			});

		this.tabElements.forEach((tabElement) => {
			tabElement.navigation.addEventListener('click', (event) => {
				event.stopPropagation();
				event.preventDefault();
				this.openTab(tabElement);
			});
		});

		const activeTabElement = this.getActiveTab();
		if (activeTabElement) {
			this.openTab(activeTabElement);
		}
	}

	private openTab(tab: TabElement): void {
		if(this.visibleTabIndex === tab.index) {
			return;
		}

		const visibleTab = this.getTabByIndex(this.visibleTabIndex);
		visibleTab?.navigation.classList.remove(this.options.activeClass);
		visibleTab?.element.addEventListener('transitionrun', () => {
			tab.navigation.classList.add(this.options.activeClass);
			tab.element.classList.add(this.options.visibleClass);
		}, {once: true});
		visibleTab?.element.classList.remove(this.options.visibleClass);
		this.visibleTabIndex = tab.index;
		this.updateHistory(tab);
		this.loadContent(tab);

		if(this.wrapperElement) {
			this.wrapperElement.style.height = (tab.element.getBoundingClientRect().height || 0) + 'px';
		}
	}

	private updateHistory(tab: TabElement) {
		if (this.options.updateHistoryOnTabChange) {
			window.history.pushState(
				'',
				tab.navigation.title,
				removeQueryStringFromPath(tab.target)
			);
		}
	}

	private loadContent(tab: TabElement) {
		if (tab.loaded) {
			return;
		}
		tab.element.innerHTML = this.options.loadingHtml;
		fetchHtml(tab.target).then((data) => {
			if (!isNullOrEmpty(data)) {
				tab.element.innerHTML = data;
				tab.loaded = true;
			}
		});
	}

	private getActiveTab() {
		return this.tabElements.find((tabElement) =>
			tabElement.navigation.classList.contains(this.options.activeClass)
		);
	}

	private getTabByIndex(index: number | null) {
		return this.tabElements.find((tabElement) => tabElement.index === index);
	}
}
